import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ControlRight, Star } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import { Link } from 'gatsby'
import React, { memo } from 'react'

export interface Props {
  areaClub?: boolean
  className?: string
  pricefrom?: string
  image?: ImageProps
  languageCode: string
  subtitle?: string
  title: string
  URL: string
}

export const Offer = memo(function Offer({
  areaClub,
  className,
  image,
  languageCode,
  pricefrom,
  subtitle,
  title,
  URL,
}: Props) {
  if (!title || !URL) {
    return null
  }

  return (
    <Container className={className} to={URL}>
      <Background>
        {image ? <Image {...image} /> : null}

        {areaClub || pricefrom ? (
          <Tags dial={6} row>
            {areaClub ? (
              <Vip dial={4} row>
                <Star />

                {useVocabularyData('area-club-only', languageCode)}
              </Vip>
            ) : null}

            {pricefrom ? <Pricefrom>{pricefrom}</Pricefrom> : null}
          </Tags>
        ) : null}
      </Background>

      <Wrapper>
        <Title>{title}</Title>

        {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}

        <ControlRight />
      </Wrapper>
    </Container>
  )
})

const Background = styled.div`
  aspect-ratio: 1.44;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}

    transition: 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  }

  @media (max-width: 991px) {
    aspect-ratio: 1.5;
  }
`

const Wrapper = styled.div`
  margin-top: 1.5rem;
  transition: 0.4s cubic-bezier(0.25, 1, 0.5, 1);

  svg {
    margin-top: 1rem;
    width: 1.5rem;
  }

  @media (max-width: 991px) {
    margin-top: 1.125rem;
  }
`

const Title = styled.div`
  ${styles.title.xs}

  color: ${({ theme }) => theme.colors.variants.neutralDark2};
`

const Subtitle = styled.div`
  ${styles.description.dark}

  margin-top: 0.375rem;
`

const Tags = styled(FlexBox)`
  position: absolute;
  top: 1rem;
  right: 1rem;
`

const Vip = styled(FlexBox)`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  border-radius: 1.5rem;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${theme.fontFamily.paragraph};
  font-size: 0.875rem;
  font-weight: 300;
  line-height: 1.125rem;
  padding: 0.5rem 1rem 0.75rem;

  svg {
    width: auto;
    height: 0.75rem;
    fill: ${({ theme }) => theme.colors.variants.primaryDark};
    margin-right: 0.5rem;
    transform: translateY(0.0625rem);
  }
`

const Pricefrom = styled.div`
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  border-radius: 1.5rem;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${theme.fontFamily.paragraph};
  font-size: 0.875rem;
  font-weight: 300;
  line-height: 1.125rem;
  margin-left: 0.375rem;
  padding: 0.5rem 1rem 0.75rem;
`

const Container = styled(Link)`
  display: block;
  text-align: left;

  &:hover {
    ${Background} {
      img {
        transform: scale(1.1);
      }
    }

    ${Wrapper} {
      transform: translateY(-0.25rem);
    }
  }
`

export type Variant = 'default' | 'slider'
